import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { BreakPoints, useBreakpoint } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { HEADER_HEIGHT, Header, REPRESENTATION_BANNER_HEIGHT } from '@component/header.component';
import { TopBar } from '@component/top_bar.component';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import { Outlet } from 'react-router';
import styled from 'styled-components';

const FLOW_LAYOUT_HEADER_GAP = '28px';

interface LayoutOutletFlowProps {
	hideHeader?: boolean;
}

const StyledBackground = styled.div<{ $hasRepresentationBanner: boolean; $hideHeader: boolean }>`
	width: 100%;
	height: 100%;
	background-color: ${tokens.color.bg.page};
	display: flex;
	flex-direction: column;

	${tokens.breakpointQuery.medium} {
		align-items: flex-start;
		padding-top: calc(
			${({ $hideHeader }) => ($hideHeader ? '0px' : HEADER_HEIGHT.medium)} + ${FLOW_LAYOUT_HEADER_GAP} +
				${({ $hasRepresentationBanner, $hideHeader }) =>
					!$hideHeader && $hasRepresentationBanner ? REPRESENTATION_BANNER_HEIGHT : '0px'}
		);
	}
`;

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	padding: ${tokens.spacing.x2};
	position: relative;
	background: ${tokens.color.bg.surface};

	${tokens.breakpointQuery.medium} {
		padding: ${tokens.spacing.x3} ${tokens.spacing.x4} ${tokens.spacing.x4};
		max-width: 580px;
		width: 100%;
		margin: 0 auto;
		border: 1px solid ${tokens.color.border.default};
		border-radius: ${tokens.borderRadius.medium};
		flex-grow: 0;
	}
`;

export function LayoutOutletFlow({ hideHeader = false }: LayoutOutletFlowProps) {
	const breakpoint = useBreakpoint();
	const featureUseRepresentatives = useFeatureFlag('PatientWeb.UseRepresentatives');
	const { currentlyRepresentedPatient } = useRepresentablePatients({ enabled: featureUseRepresentatives });

	return (
		<StyledBackground $hasRepresentationBanner={currentlyRepresentedPatient != null} $hideHeader={hideHeader}>
			<TopBar.Root>
				{!hideHeader && breakpoint > BreakPoints.Small ? <Header /> : null}
				<StyledContainer>
					<TopBar.Outlet />
					<Outlet />
				</StyledContainer>
			</TopBar.Root>
		</StyledBackground>
	);
}
